import { useState, useCallback, useEffect } from 'react';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import { readNdjsonStream } from '@/utils/readNdjsonStream';

export interface StudioTeamInput {
  title: string;
  genre: string;
  mood?: string;
  setting?: string;
  vocal?: string;
  constraints?: string;
  includeImagePrompt?: boolean;
  /** 'studio' forbids "live" language in style tags; 'live' wants concert character. Defaults to 'studio'. */
  styleMode?: 'studio' | 'live';
  /** A drawn topic-seed-bank subject — see src/hooks/useSongTopics.ts. */
  subject?: string;
}

export interface StudioTeamResult {
  provider: 'claude' | 'gemini';
  model: string;
  text: string;
  usage?: { inputTokens: number; outputTokens: number };
  /** Length of the Style block as the LLM originally produced it (before our compaction). null if not detected. */
  styleCharsOriginal?: number | null;
  /** Length of the Style block in the returned text after server-side compaction. */
  styleCharsFinal?: number | null;
  /** Length of the Excluded Style block before server-side compaction. */
  excludesCharsOriginal?: number | null;
  /** Length of the Excluded Style block after server-side compaction. */
  excludesCharsFinal?: number | null;
  /** Length of the Lyrics block before the trim/expand pass. */
  lyricsCharsOriginal?: number | null;
  /** Length after the trim/expand pass (== original if no pass was run). */
  lyricsCharsFinal?: number | null;
  /** True if the LLM trim/expand pass actually ran. */
  lyricsTrimmed?: boolean;
  /** True if the deterministic safety-net hard-trim fired (LLM trim pass left lyrics >5000). */
  lyricsHardTrimmed?: boolean;
  /** Banned-language violations (§6.3-6.5) that survived both server repair passes. Empty/absent = clean. */
  bannedLanguageRemaining?: string[];
}

// ── Module-level singleton so result + brief survive tab unmount ─────
let _result: StudioTeamResult | null = null;
let _brief: StudioTeamInput | null = null;
const _listeners = new Set<() => void>();
function notify() { _listeners.forEach(fn => fn()); }
function setCached(result: StudioTeamResult | null, brief: StudioTeamInput | null) {
  _result = result;
  _brief = brief;
  notify();
}

export function useStudioTeam() {
  const [, force] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-render this hook instance whenever the singleton changes
  useEffect(() => {
    const fn = () => force(n => n + 1);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);

  const generate = useCallback(async (input: StudioTeamInput): Promise<StudioTeamResult | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf('/api/studio-team/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || `Studio Team failed (${res.status})`);
      }

      // Response is chunked newline-delimited JSON, not a single buffered
      // body -- heartbeat lines keep the connection alive through the 2-4
      // min generation (see server/routes/studioteam.routes.ts for why).
      const result = await readNdjsonStream<StudioTeamResult>(res);
      setCached(result, input);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => { setCached(null, null); setError(null); }, []);

  /** Preload the brief without generating — used by "Send to Studio Team" from other features. */
  const setBrief = useCallback((brief: StudioTeamInput) => {
    setCached(_result, brief);
  }, []);

  return {
    result: _result,
    brief: _brief,
    loading,
    error,
    generate,
    reset,
    setBrief,
  };
}
